import { QueryHookOptions } from "@apollo/client"
import useIntersection from "@pathwright/ui/src/components/hooks/useIntersection"
import CountIndicator from "@pathwright/ui/src/components/indicator/CountIndicator"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import classnames from "classnames"
import get from "lodash/get"
import { useEffect, useState } from "react"
import styled from "styled-components"
import {
  InboxPendingReviewCountQueryVariables,
  useInboxPendingReviewCountQuery
} from "../../api/generated"
import { inboxNavigatorItemPropType } from "./propTypes"
import { InboxGroupNode } from "./useInboxItems"

const Container = styled.div`
  position: relative;

  .CountIndicator {
    position: absolute;
    top: 0.1em;
    right: 0.1em;
    transform: translate(0.5em, -0.5em);
  }
`

type UseInboxPendingReviewCountQueryOptions = Omit<
  QueryHookOptions<any, InboxPendingReviewCountQueryVariables>,
  "variables"
>

// Helper hook to wrap useInboxPendingReviewCountQuery with proper options typing.
const useInboxPendingReviewCount = (
  { targetType, targetId }: InboxPendingReviewCountQueryVariables,
  options: Partial<UseInboxPendingReviewCountQueryOptions> = {}
) => {
  const query = useInboxPendingReviewCountQuery({
    variables: {
      targetType,
      targetId
    },
    ...options
  })

  const count = get(query, "data.inboxPendingReview.count", null)
  return count
}

// Query count on intersection.
const useInboxPendingReviewCountOnIntersection = ({
  targetType,
  targetId
}: InboxPendingReviewCountQueryVariables) => {
  const [scrollNode, setScrollNode] = useState<Element | null>(null)
  const { setIntersectionNode, hasIntersected } = useIntersection({
    root: scrollNode
  })

  useEffect(() => {
    const node = document.querySelector(".InboxNavigator__items")
    if (node) setScrollNode(node)
  }, [])

  const count = useInboxPendingReviewCount(
    {
      targetType,
      targetId
    },
    {
      skip: !hasIntersected
    }
  )

  return { count, setIntersectionNode }
}

const InboxNavigatorItemReviewCount = ({
  className,
  item
}: {
  className?: string
  item: InboxGroupNode
}) => {
  const { count, setIntersectionNode } =
    useInboxPendingReviewCountOnIntersection({
      targetType: item.type,
      targetId: item.id!
    })

  return (
    <Container
      className={classnames("InboxNavigatorItemReviewCount", className)}
      ref={setIntersectionNode}
    >
      <Pathicon icon="inbox" />
      {!!count && <CountIndicator count={count} />}
    </Container>
  )
}

InboxNavigatorItemReviewCount.displayName = "InboxNavigatorItemReviewCount"

InboxNavigatorItemReviewCount.propTypes = {
  item: inboxNavigatorItemPropType.isRequired
}

export default InboxNavigatorItemReviewCount
